/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import createTheme from '@mui/material/styles/createTheme';
import JSG from '@cedalo/jsg-ui';

const createBright = () => {
	return createTheme({
		palette: {
			primary: {
				main: '#FD602E',
				// main: '#AAAAAA',
				mainText: '#333333'
			},
			error: {
				main: '#FF0022'
			}
		},
		wall: {
			backgroundColor: '#ffffff'
		},
		cellrange: {
			color: 'black',
			colorlight: 'rgba(0, 0, 0, 0.54)',
			underline: 'rgba(0, 0, 0, 0.42)'
		},
		table: {
			backgroundHeader: '#f2f2f2',
			backgroundCell: 'white',
			backgroundCellHover: '#77bbff',
			backgroundCellActive: '#5588cc'
		},
		typography: {
			fontSize: 12
		},
		components: {
			MuiAppBar: {
				styleOverrides: {
					colorPrimary: {
						backgroundColor: '#F7F9FC'
					},
					colorDefault: {
						backgroundColor: 'white'
					},
					colorEdit: {
						backgroundColor: 'white'
					},
					colorIcon: {
						backgroundColor: 'rgba(117, 117, 117'
					},
					colorCaption: {
						color: '#222222',
						backgroundColor: '#F0F0F0'
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						maxWidth: '100vw',
						maxHeight: '100vh'
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: '#FFFFFF',
						color: '#333333',
						border: '1px solid #333333',
						fontSize: '8pt',
						maxWidth: '200px',
						padding: '5px'
					},
					popper: {
						opacity: '1'
					}
				}
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						padding: '2px 4px 3px 12px'
					}
				}
			},
			MuiRadio: {
				styleOverrides: {
					root: {
						padding: '4px 4px 4px 12px'
					}
				}
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						padding: 0,
						margin: 0
					}
				}
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: '40px'
					}
				}
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						justifyContent: 'space-between',
						borderBottom: '1px solid rgba(0, 0, 0, 0.23)'
					}
				}
			},
			MuiDialogContent: {
				styleOverrides: {
					root: {
						width: 'auto'
					}
				}
			},
			MuiTableSortLabel: {
				styleOverrides: {
					icon: {
						fontSize: '0.9rem !important',
						marginLeft: '2px !important'
					}
				}
			}
		}
	});
};

const createDark = () => {
	return createTheme({
		palette: {
			mode: 'dark',
			primary: {
				// main: '#90caf9'
				main: '#FD602E',
			},
			error: {
				main: '#FF0022'
			}
		},
		wall: {
			backgroundColor: '#313131'
		},
		cellrange: {
			color: 'white',
			colorlight: 'white',
			underline: 'rgba(255, 255, 255, 0.7)'
		},
		table: {
			backgroundHeader: '#212121',
			backgroundCell: 'black',
			backgroundCellHover: '#77bbff',
			backgroundCellActive: '#5588cc'
		},
		typography: {
			fontSize: 12
		},
		components: {
			MuiAppBar: {
				styleOverrides: {
					colorPrimary: {
						backgroundColor: '#212121'
					},
					colorDefault: {
						backgroundColor: '#212121'
					},
					colorEdit: {
						backgroundColor: '#35373b'
					},
					colorIcon: {
						backgroundColor: 'rgba(255, 255, 255'
					},
					colorCaption: {
						color: 'white',
						backgroundColor: '#212121'
					}
				}
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						backgroundColor: '#212121'
					}
				}
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						maxWidth: '100vw',
						maxHeight: '100vh'
					}
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						border: '1px solid #333333',
						fontSize: '8pt',
						maxWidth: '200px',
						padding: '5px'
					},
					popper: {
						opacity: '1'
					}
				}
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						padding: '2px 4px 3px 12px',
						color: 'white'
					}
				}
			},
			MuiFormControlLabel: {
				styleOverrides: {
					root: {
						color: 'white'
					}
				}
			},
			MuiRadio: {
				styleOverrides: {
					root: {
						padding: '4px 4px 4px 12px'
					}
				}
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						padding: 0,
						margin: 0
					}
				}
			},
			MuiListItemIcon: {
				styleOverrides: {
					root: {
						minWidth: '40px'
					}
				}
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						borderBottom: '1px solid rgba(255, 255, 255, 0.23);',
						justifyContent: 'space-between'
					}
				}
			},
			MuiDialogContent: {
				styleOverrides: {
					root: {
						width: 'auto'
					}
				}
			},
			MuiTableSortLabel: {
				styleOverrides: {
					icon: {
						fontSize: '0.9rem !important',
						marginLeft: '2px !important'
					}
				}
			}
		}
	});
};

const createJSGByName = (theme, name) => {
	if (name === 'Dark') {
		return {
			chart: 'dark',
			theme: name,
			graph: '#333333',
			tool: theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
			listback: '#333333',
			listborder: '#777777',
			caption: theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
			captiontext: '#FFFFFF',
			frame: '#FFFFFF',
			splitter: '#333333',
			header: theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
			headertext: '#CCCCCC',
			outline: '#CCCCCC',
			sheet: '#000000',
			sheetbackground: '#222222',
			grid: '#777777',
			border: '#FFFFFF',
			text: '#FFFFFF',
			textlight: '#DDDDDD',
			textdisabled: '#AAAAAA',
			fill: '#000000',
			filllight: '#222222',
			selectionback: '#606060',
			feedbackFill: 'rgba(255, 255, 255,0.2)',
			feedbackBorder: 'rgba(255, 255, 255,0.8)',
			scrollBars: {
				arrow: '#999999',
				arrowbk: '#555555',
				range: '#000000',
				background: '#000000',
				thumb: '#555555'
			},
			rangeColors: [
				'#00B0F0',
				'#00FF00',
				'#FFFF00',
				'#ED7D31',
				'#00CC99',
				'#FF99FF',
				'#CCFFCC',
				'#FF3737',
				'#A5A5A5',
				'#FF0066'
			],
			json: {},
			ifTransparency: 60
		};
	}

	return {
		theme: name,
		chart: 'basic',
		graph: '#FFFFFF',
		tool: theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
		listback: '#FFFFFF',
		listborder: '#333333',
		caption: theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor,
		captiontext: name === 'Light' ? '#222222' : '#FFFFFF',
		frame: '#AAAAAA',
		splitter: '#FFFFFF',
		header: '#F7F9FC',
		headertext: '#333333',
		outline: '#777777',
		sheet: '#FFFFFF',
		sheetbackground: '#EEEEEE',
		grid: '#CCCCCC',
		border: '#000000',
		text: '#000000',
		textlight: '#222222',
		textdisabled: '#AAAAAA',
		fill: '#FFFFFF',
		filllight: '#F1F1F1',
		selectionback: '#EBEBEB',
		feedbackFill: 'rgba(0,0,0,0.2)',
		feedbackBorder: 'rgba(0,0,0,0.8)',
		scrollBars: {
			arrow: '#777777',
			arrowbk: '#F3F3F3',
			range: '#F3F3F3',
			background: '#EEEEEE',
			thumb: '#CCCCCC'
		},
		rangeColors: [
			'#1718D1',
			'#A61D4C',
			'#8CC474',
			'#F75C02',
			'#2FB4D2',
			'#803995',
			'#00B15B',
			'#795548',
			'#FABB69',
			'#168D9A'
		],
		ifTransparency: 17
	};
};

const createByName = () => {
	let theme;
	let name = localStorage.getItem('theme');

	if (name === 'Default') {
		if (window.matchMedia) {
			if(window.matchMedia('(prefers-color-scheme: dark)').matches){
				name = 'Dark';
			} else {
				name = 'Light';
			}
		} else {
			name = 'Light';
		}
	}

	switch (name) {
		case 'Dark':
			theme = createDark();
			break;
		default:
			name = 'Light';
			theme = createBright();
			break;
	}

	JSG.theme = createJSGByName(theme, name);

	JSG.FormatAttributes.template = JSG.FormatAttributes.createTemplate();
	JSG.TextFormatAttributes.template = JSG.TextFormatAttributes.createTemplate();

	return theme;
};

const theme = createByName();

export default theme;
